import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import HomeworkCreateModal from "@/pages/ogretmen/components/HomeworkCreateModal";
import {
  useTeacherStudents,
  useTeacherHomework,
  TEACHER_HOMEWORK_STATUS_LABELS,
  TEACHER_HOMEWORK_STATUS_COLORS,
} from "@/hooks/useTeacher";

export default function Odevler() {
  const { data: students, loading: studentsLoading } = useTeacherStudents();
  const { data, loading, error, reload } = useTeacherHomework();
  const [createOpen, setCreateOpen] = useState(false);

  const list = data ?? [];
  const pending = list.filter((h) => h.status !== "tamamlandi").length;
  const done = list.length - pending;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Ödevler"
          description="Ödevleri oluştur ve teslimatları takip et."
        />
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
        >
          <i className="ri-add-line" aria-hidden="true"></i>
          Ödev Oluştur
        </button>
      </div>

      {loading || studentsLoading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-task-line"
          title="Henüz ödev yok"
          description='"Ödev Oluştur" ile öğrencilerine ödev ver, teslim durumunu buradan takip et.'
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs font-medium text-foreground-500">Toplam teslimat</p>
              <p className="mt-1 font-heading font-bold text-2xl text-foreground-950">{list.length}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs font-medium text-foreground-500">Tamamlanan</p>
              <p className="mt-1 font-heading font-bold text-2xl text-accent-700">{done}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs font-medium text-foreground-500">Bekleyen</p>
              <p className="mt-1 font-heading font-bold text-2xl text-foreground-950">{pending}</p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/60">
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Öğrenci</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Ödev</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Ders</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Teslim</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((h) => (
                    <tr key={h.id} className="border-b border-background-200 hover:bg-background-100/40 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-950">{h.studentName}</td>
                      <td className="px-4 py-3 text-foreground-700">{h.title}</td>
                      <td className="px-4 py-3 text-foreground-600">{h.subject}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{h.dueDate || "—"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${TEACHER_HOMEWORK_STATUS_COLORS[h.status] ?? "bg-background-100 text-foreground-600"}`}
                        >
                          {TEACHER_HOMEWORK_STATUS_LABELS[h.status] ?? h.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <PanelLinks
        links={[
          { label: "Öğrencilerim", href: "/ogretmen/ogrencilerim", icon: "ri-group-line" },
          { label: "Öğrenci Analizleri", href: "/ogretmen/ogrenci-analizleri", icon: "ri-line-chart-line" },
        ]}
      />

      <HomeworkCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={reload}
        students={students ?? []}
      />
    </div>
  );
}